import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Grid,
  Box,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import RefreshIcon from "@material-ui/icons/Refresh";
import VisibilityIcon from "@material-ui/icons/Visibility";
import UndoIcon from "@material-ui/icons/Undo";
import api from "../../services/api";
import { toast } from "react-toastify";
import moment from "moment";
import ConfirmationModal from "../../components/ConfirmationModal";
import { i18n } from "../../translate/i18n";
import MainContainer from "../../components/MainContainer";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  tableHeader: {
    fontWeight: 'bold',
    backgroundColor: "#1e1e1e",
    color: "#fff",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  cyanButton: {
    backgroundColor: '#00b0ff',
    color: '#fff',
    borderRadius: '4px',
    textTransform: 'none',
    fontWeight: 'bold',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#0091ea',
      boxShadow: '0 4px 12px rgba(0, 176, 255, 0.4)',
      transform: 'translateY(-2px)'
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: 'none'
    }
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    marginBottom: theme.spacing(3),
    fontWeight: 700,
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  tableContainer: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    borderRadius: "10px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
  },
  revertBtn: {
    fontWeight: 700,
    fontSize: "0.7rem",
    background: "#f44336",
    color: "#fff",
    "&:hover": {
      background: "#d32f2f",
    },
    "&:disabled": {
      background: "#e0e0e0",
      color: "#9e9e9e",
    }
  }
}));

const AdminResellerPanel = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [resellers, setResellers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [transactionToRevert, setTransactionToRevert] = useState(null);
  const [reverting, setReverting] = useState(false);
  const [selectedClientsModal, setSelectedClientsModal] = useState({ open: false, resellerName: "", clients: [] });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resellersRes, txRes] = await Promise.all([
        api.get("/admin/resellers"),
        api.get("/admin/resellers/transactions")
      ]);
      setResellers(resellersRes.data);
      setTransactions(txRes.data);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar los datos del panel");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenRevert = (tx) => {
    setTransactionToRevert(tx);
    setConfirmModalOpen(true);
  };

  const handleConfirmRevert = async () => {
    if (!transactionToRevert) return;
    setReverting(true);
    try {
      await api.post(`/admin/resellers/revert/${transactionToRevert.id}`);
      toast.success("Transacción revertida con éxito");
      setConfirmModalOpen(false);
      setTransactionToRevert(null);
      fetchData(); // Refresh data
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.error || "Error al revertir";
      toast.error(msg);
    } finally {
      setReverting(false);
    }
  };

  if (!user?.super) {
    return <Typography>{i18n.t("adminReseller.denied")}</Typography>;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <MainContainer>
      <Paper className={classes.mainPaper} elevation={0}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" className={classes.header}>
            👑 {i18n.t("adminReseller.title")}
          </Typography>
          <Button size="small" onClick={fetchData} startIcon={<RefreshIcon />} variant="contained" className={classes.cyanButton}>
            {i18n.t("adminReseller.updateData")}
          </Button>
        </Box>
        <Box style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }} mb={1}>
            <Typography className={classes.sectionTitle}>
              <AccountBalanceWalletIcon fontSize="small" />
              {i18n.t("adminReseller.resellersTitle")} ({resellers.length})
            </Typography>
            <TableContainer component={Paper} className={classes.tableContainer} variant="outlined">
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeader}>{i18n.t("adminReseller.headers.id")}</TableCell>
                    <TableCell className={classes.tableHeader}>{i18n.t("adminReseller.headers.name")}</TableCell>
                    <TableCell className={classes.tableHeader}>{i18n.t("adminReseller.headers.discount")}</TableCell>
                    <TableCell className={classes.tableHeader}>{i18n.t("adminReseller.headers.balance")}</TableCell>
                    <TableCell className={classes.tableHeader}>{i18n.t("adminReseller.headers.investment")}</TableCell>
                    <TableCell className={classes.tableHeader}>{i18n.t("adminReseller.headers.dueDate")}</TableCell>
                    <TableCell className={classes.tableHeader} align="center">{i18n.t("adminReseller.headers.referredClients")}</TableCell>
                    <TableCell className={classes.tableHeader} align="center">{i18n.t("adminReseller.headers.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resellers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" style={{ padding: "32px 0" }}>
                        <Typography color="textSecondary">{i18n.t("adminReseller.noResellers")}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    resellers.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell>{r.id}</TableCell>
                        <TableCell><strong>{r.name}</strong></TableCell>
                        <TableCell>{r.discountPercentage}%</TableCell>
                        <TableCell style={{ color: "#4caf50", fontWeight: "bold" }}>
                          ${parseFloat(r.resellerBalance || 0).toFixed(2)}
                        </TableCell>
                        <TableCell style={{ color: "#757575", fontWeight: "bold" }}>
                          ${(parseFloat(r.resellerBalance || 0) * (1 - (r.discountPercentage / 100))).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {r.dueDate ? moment(r.dueDate).format("DD/MM/YYYY") : "—"}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={r.referredCount || 0} size="small" />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={i18n.t("adminReseller.viewClients")}>
                            <IconButton
                              size="small"
                              onClick={() => setSelectedClientsModal({ open: true, resellerName: r.name, clients: r.referredCompanies || [] })}
                              color="primary"
                              disabled={!r.referredCompanies || r.referredCompanies.length === 0}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
        </Box>

        <Box mt={1} flex={1} display="flex" flexDirection="column" overflow="hidden">
          <Typography className={classes.sectionTitle}>
              <AccountBalanceWalletIcon fontSize="small" />
              {i18n.t("adminReseller.transactionsTitle")}
            </Typography>
            <TableContainer component={Paper} className={classes.tableContainer} variant="outlined">
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeader}>{i18n.t("adminReseller.headers.txId")}</TableCell>
                    <TableCell className={classes.tableHeader}>{i18n.t("adminReseller.headers.date")}</TableCell>
                    <TableCell className={classes.tableHeader}>{i18n.t("adminReseller.headers.reseller")}</TableCell>
                    <TableCell className={classes.tableHeader}>{i18n.t("adminReseller.headers.client")}</TableCell>
                    <TableCell className={classes.tableHeader}>{i18n.t("adminReseller.headers.type")}</TableCell>
                    <TableCell className={classes.tableHeader}>{i18n.t("adminReseller.headers.amount")}</TableCell>
                    <TableCell className={classes.tableHeader}>{i18n.t("adminReseller.headers.description")}</TableCell>
                    <TableCell className={classes.tableHeader} align="center"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" style={{ padding: "32px 0" }}>
                        <Typography color="textSecondary">{i18n.t("adminReseller.noTransactions")}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((t) => {
                      const isReverted = t.description && t.description.includes("[REVERTIDA]");
                      const hoursDiff = (new Date().getTime() - new Date(t.createdAt).getTime()) / (1000 * 60 * 60);
                      const canRevert = t.type === "consumption" && !isReverted && hoursDiff <= 24;

                      return (
                        <TableRow key={t.id} hover>
                          <TableCell>{t.id}</TableCell>
                          <TableCell>{moment(t.createdAt).format("DD/MM/YYYY HH:mm")}</TableCell>
                          <TableCell>{t.reseller?.name || t.resellerId}</TableCell>
                          <TableCell>{t.company?.name || t.companyId}</TableCell>
                          <TableCell>
                            <Chip
                              label={t.type.toUpperCase()}
                              size="small"
                              style={{
                                backgroundColor: t.type === "recharge" ? "#e3f2fd" : "#fce4ec",
                                color: t.type === "recharge" ? "#1565c0" : "#b71c1c",
                                fontWeight: 700,
                                fontSize: "0.65rem"
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <span style={{ color: t.type === "recharge" ? "#4caf50" : "#f44336", fontWeight: "bold" }}>
                              {t.type === "recharge" ? "+" : "-"}${parseFloat(t.amount).toFixed(2)}
                            </span>
                          </TableCell>
                          <TableCell>{t.description}</TableCell>
                          <TableCell align="center">
                            {canRevert && (
                              <Tooltip title={i18n.t("adminReseller.revertSale")}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenRevert(t)}
                                  style={{ color: "#f44336" }}
                                >
                                  <UndoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
      </Paper>

      {/* Clients Modal */}
      <Dialog
        open={selectedClientsModal.open}
        onClose={() => setSelectedClientsModal({ open: false, resellerName: "", clients: [] })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Clientes de: {selectedClientsModal.resellerName}</DialogTitle>
        <DialogContent dividers>
          <TableContainer component={Paper} variant="outlined">
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeader}>ID</TableCell>
                  <TableCell className={classes.tableHeader}>Nombre</TableCell>
                  <TableCell className={classes.tableHeader}>Email</TableCell>
                  <TableCell className={classes.tableHeader}>Teléfono</TableCell>
                  <TableCell className={classes.tableHeader}>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedClientsModal.clients.map(c => (
                  <TableRow key={c.id}>
                    <TableCell>{c.id}</TableCell>
                    <TableCell><strong>{c.name}</strong></TableCell>
                    <TableCell>{c.email}</TableCell>
                    <TableCell>{c.phone}</TableCell>
                    <TableCell>
                      <Chip
                        label={c.status ? "Activo" : "Inactivo"}
                        size="small"
                        style={{
                          backgroundColor: c.status ? "#e8f5e9" : "#fce4ec",
                          color: c.status ? "#2e7d32" : "#c62828"
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedClientsModal({ open: false, resellerName: "", clients: [] })} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Confirmar Reversión"
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleConfirmRevert}
      >
        <Typography align="center" style={{ margin: "0 auto", display: "block" }}>
          ¿Estás seguro de que deseas <strong>revertir</strong> esta venta?
          <br /><br />
          Se devolverán <strong>${parseFloat(transactionToRevert?.amount || 0).toFixed(2)}</strong> al saldo del distribuidor y el plan del cliente vencerá inmediatamente.
        </Typography>
      </ConfirmationModal>
    </MainContainer>
  );
};

export default AdminResellerPanel;
